/**
 * Model and agent profile constants
 * Claude models, OpenAI Codex models, thinking levels, memory backends, and agent profiles
 */

import type { AgentProfile, PhaseModelConfig, FeatureModelConfig, FeatureThinkingConfig } from '../types/settings';

// ============================================
// Provider Types
// ============================================

export type LLMProvider = 'claude' | 'codex' | 'openai_api';

// ============================================
// Available Models by Provider
// ============================================

// Claude models (Anthropic subscription)
export const CLAUDE_MODELS = [
  { value: 'opus', label: 'Claude Opus 4.5', provider: 'claude' as const },
  { value: 'sonnet', label: 'Claude Sonnet 4.5', provider: 'claude' as const },
  { value: 'haiku', label: 'Claude Haiku 4.5', provider: 'claude' as const }
] as const;

// OpenAI Codex models (ChatGPT Pro/Plus subscription - included free)
// These are the allowed models from opencode/codex.ts
export const CODEX_MODELS = [
  { value: 'gpt-5.1-codex-max', label: 'GPT-5.1 Codex Max', provider: 'codex' as const },
  { value: 'gpt-5.2-codex', label: 'GPT-5.2 Codex', provider: 'codex' as const },
  { value: 'gpt-5.2', label: 'GPT-5.2', provider: 'codex' as const },
  { value: 'gpt-5.1-codex', label: 'GPT-5.1 Codex', provider: 'codex' as const },
  { value: 'gpt-5.1-codex-mini', label: 'GPT-5.1 Codex Mini', provider: 'codex' as const },
] as const;

// OpenAI API models (pay-per-use - requires API key)
export const OPENAI_API_MODELS = [
  { value: 'gpt-5', label: 'GPT-5', provider: 'openai_api' as const },
  { value: 'gpt-5-mini', label: 'GPT-5 Mini', provider: 'openai_api' as const },
  { value: 'gpt-4o', label: 'GPT-4o', provider: 'openai_api' as const },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini', provider: 'openai_api' as const },
  { value: 'o3', label: 'o3', provider: 'openai_api' as const },
  { value: 'o4-mini', label: 'o4 Mini', provider: 'openai_api' as const },
] as const;

// Combined models for backward compatibility
export const AVAILABLE_MODELS = CLAUDE_MODELS;

// Get models for a specific provider
export function getModelsForProvider(provider: LLMProvider) {
  switch (provider) {
    case 'codex':
      return CODEX_MODELS;
    case 'openai_api':
      return OPENAI_API_MODELS;
    case 'claude':
    default:
      return CLAUDE_MODELS;
  }
}

// Get all models across all providers
export function getAllModels() {
  return [...CLAUDE_MODELS, ...CODEX_MODELS, ...OPENAI_API_MODELS];
}

// Maps model shorthand to actual model IDs (for Claude shorthand only)
// Codex and OpenAI API models use their full IDs directly
export const MODEL_ID_MAP: Record<string, string> = {
  // Claude models (shorthand -> full ID)
  opus: 'claude-opus-4-5-20251101',
  sonnet: 'claude-sonnet-4-5-20250929',
  haiku: 'claude-haiku-4-5-20251001',
} as const;

// Get default model for a provider
export function getDefaultModelForProvider(provider: LLMProvider): string {
  switch (provider) {
    case 'codex':
      return 'gpt-5.2-codex';
    case 'openai_api':
      return 'gpt-5';
    case 'claude':
    default:
      return 'sonnet';
  }
}

// Maps thinking levels to budget tokens (null = no extended thinking)
export const THINKING_BUDGET_MAP: Record<string, number | null> = {
  none: null,
  low: 1024,
  medium: 4096,
  high: 16384,
  ultrathink: 63999 // Maximum reasoning depth (API requires max_tokens >= budget + 1, so 63999 + 1 = 64000 limit)
} as const;

// ============================================
// Thinking Levels
// ============================================

// Thinking levels for Claude model (budget token allocation)
export const THINKING_LEVELS = [
  { value: 'none', label: 'None', description: 'No extended thinking' },
  { value: 'low', label: 'Low', description: 'Brief consideration' },
  { value: 'medium', label: 'Medium', description: 'Moderate analysis' },
  { value: 'high', label: 'High', description: 'Deep thinking' },
  { value: 'ultrathink', label: 'Ultra Think', description: 'Maximum reasoning depth' }
] as const;

// ============================================
// Agent Profiles - Phase Configurations
// ============================================

// Phase configurations for each preset profile
// Each profile has its own default phase models and thinking levels

// Auto (Optimized) - Opus with optimized thinking per phase
export const AUTO_PHASE_MODELS: PhaseModelConfig = {
  spec: 'opus',
  planning: 'opus',
  coding: 'opus',
  qa: 'opus'
};

export const AUTO_PHASE_THINKING: import('../types/settings').PhaseThinkingConfig = {
  spec: 'ultrathink',   // Deep thinking for comprehensive spec creation
  planning: 'high',     // High thinking for planning complex features
  coding: 'low',        // Faster coding iterations
  qa: 'low'             // Efficient QA review
};

// Complex Tasks - Opus with ultrathink across all phases
export const COMPLEX_PHASE_MODELS: PhaseModelConfig = {
  spec: 'opus',
  planning: 'opus',
  coding: 'opus',
  qa: 'opus'
};

export const COMPLEX_PHASE_THINKING: import('../types/settings').PhaseThinkingConfig = {
  spec: 'ultrathink',
  planning: 'ultrathink',
  coding: 'ultrathink',
  qa: 'ultrathink'
};

// Balanced - Sonnet with medium thinking across all phases
export const BALANCED_PHASE_MODELS: PhaseModelConfig = {
  spec: 'sonnet',
  planning: 'sonnet',
  coding: 'sonnet',
  qa: 'sonnet'
};

export const BALANCED_PHASE_THINKING: import('../types/settings').PhaseThinkingConfig = {
  spec: 'medium',
  planning: 'medium',
  coding: 'medium',
  qa: 'medium'
};

// Quick Edits - Haiku with low thinking across all phases
export const QUICK_PHASE_MODELS: PhaseModelConfig = {
  spec: 'haiku',
  planning: 'haiku',
  coding: 'haiku',
  qa: 'haiku'
};

export const QUICK_PHASE_THINKING: import('../types/settings').PhaseThinkingConfig = {
  spec: 'low',
  planning: 'low',
  coding: 'low',
  qa: 'low'
};

// Default phase configuration (used for fallback, matches 'Balanced' profile for cost-effectiveness)
export const DEFAULT_PHASE_MODELS: PhaseModelConfig = BALANCED_PHASE_MODELS;
export const DEFAULT_PHASE_THINKING: import('../types/settings').PhaseThinkingConfig = BALANCED_PHASE_THINKING;

// ============================================
// Feature Settings (Non-Pipeline Features)
// ============================================

// Default feature model configuration (for insights, ideation, roadmap, github, utility)
export const DEFAULT_FEATURE_MODELS: FeatureModelConfig = {
  insights: 'sonnet',     // Fast, responsive chat
  ideation: 'opus',       // Creative ideation benefits from Opus
  roadmap: 'opus',        // Strategic planning benefits from Opus
  githubIssues: 'opus',   // Issue triage and analysis benefits from Opus
  githubPrs: 'opus',      // PR review benefits from thorough Opus analysis
  utility: 'haiku'        // Fast utility operations (commit messages, merge resolution)
};

// Default feature thinking configuration
export const DEFAULT_FEATURE_THINKING: FeatureThinkingConfig = {
  insights: 'medium',     // Balanced thinking for chat
  ideation: 'high',       // Deep thinking for creative ideas
  roadmap: 'high',        // Strategic thinking for roadmap
  githubIssues: 'medium', // Moderate thinking for issue analysis
  githubPrs: 'medium',    // Moderate thinking for PR review
  utility: 'low'          // Fast thinking for utility operations
};

// Feature labels for UI display
export const FEATURE_LABELS: Record<keyof FeatureModelConfig, { label: string; description: string }> = {
  insights: { label: 'Insights Chat', description: 'Ask questions about your codebase' },
  ideation: { label: 'Ideation', description: 'Generate feature ideas and improvements' },
  roadmap: { label: 'Roadmap', description: 'Create strategic feature roadmaps' },
  githubIssues: { label: 'GitHub Issues', description: 'Automated issue triage and labeling' },
  githubPrs: { label: 'GitHub PR Review', description: 'AI-powered pull request reviews' },
  utility: { label: 'Utility', description: 'Commit messages and merge conflict resolution' }
};

// Default agent profiles for preset model/thinking configurations
// All profiles have per-phase configuration for full customization
export const DEFAULT_AGENT_PROFILES: AgentProfile[] = [
  {
    id: 'auto',
    name: 'Auto (Optimized)',
    description: 'Uses Opus across all phases with optimized thinking levels',
    model: 'opus',
    thinkingLevel: 'high',
    icon: 'Sparkles',
    phaseModels: AUTO_PHASE_MODELS,
    phaseThinking: AUTO_PHASE_THINKING
  },
  {
    id: 'complex',
    name: 'Complex Tasks',
    description: 'For intricate, multi-step implementations requiring deep analysis',
    model: 'opus',
    thinkingLevel: 'ultrathink',
    icon: 'Brain',
    phaseModels: COMPLEX_PHASE_MODELS,
    phaseThinking: COMPLEX_PHASE_THINKING
  },
  {
    id: 'balanced',
    name: 'Balanced',
    description: 'Good balance of speed and quality for most tasks',
    model: 'sonnet',
    thinkingLevel: 'medium',
    icon: 'Scale',
    phaseModels: BALANCED_PHASE_MODELS,
    phaseThinking: BALANCED_PHASE_THINKING
  },
  {
    id: 'quick',
    name: 'Quick Edits',
    description: 'Fast iterations for simple changes and quick fixes',
    model: 'haiku',
    thinkingLevel: 'low',
    icon: 'Zap',
    phaseModels: QUICK_PHASE_MODELS,
    phaseThinking: QUICK_PHASE_THINKING
  }
];

// ============================================
// Memory Backends
// ============================================

export const MEMORY_BACKENDS = [
  { value: 'file', label: 'File-based (default)' },
  { value: 'graphiti', label: 'Graphiti (LadybugDB)' }
] as const;
